import { useCallback } from 'react';
import without from 'lodash/without';
import {
  getTableIndex,
  createPeriodFilters,
  createResetFilters,
  createResetPeriod,
  createInitialColumnState,
} from '@/creditflowresearch/application/League/Scorecard/utils/scorecardUtils';
import { IColumnState, ISelectedPeriod } from '@/creditflowresearch/application/League/interfaces';
import { IPeriod } from '@/creditflowresearch/domain/vo/League/IStandings';

interface IUseScorecardTableProps {
  state: {
    columnState: IColumnState,
    selectedPeriod: ISelectedPeriod,
    expandedCategories: string[],
  },
  data: {
    periods: IPeriod[],
    type: string,
  },
  actions: {
    updateFilters: (filters: Record<string, any>) => Record<string, any>,
  },
  setters: {
    setColumnState: (state: IColumnState | ((prev: IColumnState) => IColumnState)) => void,
    setSelectedPeriod: (period: ISelectedPeriod) => void,
    setExpandedCategories: (categories: string[]) => void,
  },
}

export const useScorecardTable = (dependencies: IUseScorecardTableProps) => {
  const { state, data, actions, setters } = dependencies;

  const selectPeriod = useCallback((payload: ISelectedPeriod) => {
    const periodFilters = createPeriodFilters(payload, data.type);
    setters.setSelectedPeriod(payload);
    return actions.updateFilters(periodFilters);
  }, [data.type, setters.setSelectedPeriod, actions.updateFilters]);

  const resetSelectedPeriod = useCallback(() => {
    const resetFilters = createResetFilters();
    setters.setSelectedPeriod(createResetPeriod());
    return actions.updateFilters(resetFilters);
  }, [setters.setSelectedPeriod, actions.updateFilters]);

  const increaseVolumeTablePosition = useCallback(() => {
    const { valueTableIndex, valueTableColumnCount } = state.columnState;

    if ((valueTableIndex + 1 + valueTableColumnCount) <= data.periods.length) {
      setters.setColumnState((prev) => ({
        ...prev,
        valueTableIndex: valueTableIndex + 1,
      }));
    }
  }, [state.columnState, data.periods.length, setters.setColumnState]);

  const decreaseVolumeTablePosition = useCallback(() => {
    const { valueTableIndex } = state.columnState;

    if (valueTableIndex > 0) {
      setters.setColumnState((prev) => ({
        ...prev,
        valueTableIndex: valueTableIndex - 1,
      }));
    }
  }, [state.columnState, setters.setColumnState]);

  const increaseRankTablePosition = useCallback(() => {
    const { rankTableIndex, rankTableColumnCount } = state.columnState;

    if ((rankTableIndex + 1 + rankTableColumnCount) <= data.periods.length) {
      setters.setColumnState((prev) => ({
        ...prev,
        rankTableIndex: rankTableIndex + 1,
      }));
    }
  }, [state.columnState, data.periods.length, setters.setColumnState]);

  const decreaseRankTablePosition = useCallback(() => {
    const { rankTableIndex } = state.columnState;

    if (rankTableIndex > 0) {
      setters.setColumnState((prev) => ({
        ...prev,
        rankTableIndex: rankTableIndex - 1,
      }));
    }
  }, [state.columnState, setters.setColumnState]);

  const setRankColumnCount = useCallback((rankTableColumnCount: number) => {
    const { rankTableIndex } = state.columnState;
    const newRankTableIndex = getTableIndex(rankTableIndex, rankTableColumnCount, data.periods.length);

    setters.setColumnState((prev) => ({
      ...prev,
      rankTableColumnCount,
      rankTableIndex: newRankTableIndex,
    }));
  }, [state.columnState, data.periods.length, setters.setColumnState]);

  const setValuesTableColumnCount = useCallback((valueTableColumnCount: number) => {
    const { valueTableIndex } = state.columnState;
    const newValueTableIndex = getTableIndex(valueTableIndex, valueTableColumnCount, data.periods.length);

    setters.setColumnState((prev) => ({
      ...prev,
      valueTableColumnCount,
      valueTableIndex: newValueTableIndex,
    }));
  }, [state.columnState, data.periods.length, setters.setColumnState]);

  const toggleCategoryExpansion = useCallback((categoryId: string) => {
    const newExpandedCategories = state.expandedCategories.includes(categoryId)
      ? without(state.expandedCategories, categoryId)
      : [...state.expandedCategories, categoryId];

    setters.setExpandedCategories(newExpandedCategories);
  }, [state.expandedCategories, setters.setExpandedCategories]);

  const resetScorecardTableStates = useCallback(() => {
    resetSelectedPeriod();
    setters.setExpandedCategories([]);
    setters.setColumnState(createInitialColumnState());
  }, [resetSelectedPeriod, setters.setExpandedCategories, setters.setColumnState]);

  return {
    selectPeriod,
    resetSelectedPeriod,
    increaseVolumeTablePosition,
    decreaseVolumeTablePosition,
    increaseRankTablePosition,
    decreaseRankTablePosition,
    setRankColumnCount,
    setValuesTableColumnCount,
    toggleCategoryExpansion,
    resetScorecardTableStates,
  };
};
